import { Functions, httpsCallable } from 'firebase/functions';
import type {
  AiClient,
  GenerateStoryboardScriptInput,
  RegeneratePanelScriptInput,
  GeneratePanelImageInput,
  RefinePanelImageInput,
  WritersRoomInput,
  GenerateStylePreviewInput,
  GenerateEntityVisualInput,
  UploadAndResizeImageInput,
  GenerateCharacterFieldsInput,
  ProcessTextInput
} from '../contracts/ai';
import type { StoryboardStrip, StoryboardPanel } from '@/lib/storyboard-types';
import type { ProcessedImage } from '@/lib/types';

export class FirebaseAiClient implements AiClient {
  constructor(private functions: Functions) {}

  async analyzeScene(params: { userId: string; novelId: string; sceneId: string }): Promise<unknown> {
    const fn = httpsCallable(this.functions, 'analyzeScene');
    const result = await fn(params);
    return result.data;
  }

  async generateStoryboardScript(params: GenerateStoryboardScriptInput): Promise<{ strips: StoryboardStrip[] }> {
    const fn = httpsCallable(this.functions, 'generateStoryboardScript');
    const result = await fn(params);
    return result.data as { strips: StoryboardStrip[] };
  }

  async regeneratePanelScript(params: RegeneratePanelScriptInput): Promise<{ panel: StoryboardPanel }> {
    const fn = httpsCallable(this.functions, 'regeneratePanelScript');
    const result = await fn(params);
    return result.data as { panel: StoryboardPanel };
  }

  async generatePanelImage(params: GeneratePanelImageInput): Promise<{ imageUrl: string; storagePath: string; iterationId: string }> {
    const fn = httpsCallable(this.functions, 'generatePanelImage');
    const result = await fn(params);
    return result.data as { imageUrl: string; storagePath: string; iterationId: string };
  }

  async refinePanelImage(params: RefinePanelImageInput): Promise<{ imageUrl: string; storagePath: string; iterationId: string }> {
    const fn = httpsCallable(this.functions, 'refinePanelImage');
    const result = await fn(params);
    return result.data as { imageUrl: string; storagePath: string; iterationId: string };
  }

  async executeWritersRoom(params: WritersRoomInput): Promise<void> {
    const fn = httpsCallable(this.functions, 'writersRoom');
    await fn(params);
  }

  async generateStylePreview(params: GenerateStylePreviewInput): Promise<{ imageUrl: string; storagePath: string }> {
    const fn = httpsCallable(this.functions, 'generateStylePreview');
    const result = await fn(params);
    return result.data as { imageUrl: string; storagePath: string };
  }

  async generateEntityVisual(params: GenerateEntityVisualInput): Promise<{ imageUrl: string; storagePath: string }> {
    const fn = httpsCallable(this.functions, 'generateEntityVisual');
    const result = await fn(params);
    return result.data as { imageUrl: string; storagePath: string };
  }

  async uploadAndResizeImage(params: UploadAndResizeImageInput): Promise<{ images: ProcessedImage }> {
    const fn = httpsCallable(this.functions, 'uploadAndResizeImage');
    const result = await fn(params);
    return result.data as { images: ProcessedImage };
  }

  async expandStorageLayout(params: { userId: string; novelId: string; targetBlock: string }): Promise<void> {
    const fn = httpsCallable(this.functions, 'expandStorageLayout');
    await fn(params);
  }

  async generateCharacterFields(params: GenerateCharacterFieldsInput): Promise<{ suggestions?: Record<string, string> }> {
    const fn = httpsCallable(this.functions, 'generateCharacterFields');
    const result = await fn(params);
    return result.data as { suggestions?: Record<string, string> };
  }

  async processText(params: ProcessTextInput): Promise<{ result: string }> {
    const fn = httpsCallable(this.functions, 'processText');
    const result = await fn(params);
    return result.data as { result: string };
  }

  async startNovelKnowledgeBuild(params: { novelId: string; text: string }): Promise<{ sessionId: string }> {
    const fn = httpsCallable(this.functions, 'startNovelKnowledgeBuild');
    const result = await fn(params);
    return result.data as { sessionId: string };
  }

  async commitNovelKnowledgePart(params: { novelId: string; sessionId: string }): Promise<{ hasMoreParts: boolean }> {
    const fn = httpsCallable(this.functions, 'commitNovelKnowledgePart');
    const result = await fn(params);
    return result.data as { hasMoreParts: boolean };
  }
}
